import { Loader2, MapPin, Search } from 'lucide-react'
import { FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { toast } from 'sonner'

import { LogoIcon } from '@/assets/icons'
import Footer from '@/components/footer'
import Header from '@/components/header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatHoursSnippet } from '@/lib/working-hours'
import { useAppDispatch } from '@/hooks'
import { setCurrentStore } from '@/slices/store'
import storeService, { PublicStore } from '@/services/store-service'

export const Home = () => {
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const [q, setQ] = useState('')
	const [city, setCity] = useState('')
	const [zip, setZip] = useState('')
	const [nearMe, setNearMe] = useState<{ lat: number; lng: number } | null>(null)
	const [submitted, setSubmitted] = useState({ q: '', city: '', zip: '', lat: '', lng: '' })

	const { data: stores = [], isLoading, isFetching, refetch } = useQuery(
		['marketplace-stores', submitted],
		() => {
			const params: Record<string, string> = {}
			if (submitted.q) params.q = submitted.q
			if (submitted.city) params.city = submitted.city
			if (submitted.zip) params.zip = submitted.zip
			if (submitted.lat && submitted.lng) {
				params.lat = submitted.lat
				params.lng = submitted.lng
				params.radius = '50'
			}
			return storeService.searchStores(params)
		},
		{
			refetchOnWindowFocus: false,
			onError: () => toast.error('Stores konnten nicht geladen werden'),
		},
	)

	useEffect(() => {
		dispatch(setCurrentStore(null))
	}, [dispatch])

	const onSearch = (e?: FormEvent) => {
		e?.preventDefault()
		setSubmitted({
			q: q.trim(),
			city: city.trim(),
			zip: zip.trim(),
			lat: nearMe ? String(nearMe.lat) : '',
			lng: nearMe ? String(nearMe.lng) : '',
		})
	}

	const useNearMe = () => {
		if (!navigator.geolocation) {
			toast.error('Standort wird von diesem Gerät nicht unterstützt')
			return
		}
		navigator.geolocation.getCurrentPosition(
			pos => {
				const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude }
				setNearMe(coords)
				setSubmitted({
					q: q.trim(),
					city: city.trim(),
					zip: zip.trim(),
					lat: String(coords.lat),
					lng: String(coords.lng),
				})
				toast.success('Suche in deiner Nähe')
			},
			() => toast.error('Standort konnte nicht ermittelt werden'),
			{ enableHighAccuracy: false, timeout: 10000 },
		)
	}

	const openStore = (store: PublicStore) => {
		dispatch(
			setCurrentStore({
				uuid: store.uuid,
				slug: store.slug,
				name: store.name,
				logo_url: store.logo_url,
				tagline: store.tagline,
				city: store.city,
				zip: store.zip,
				address: store.address,
				phone: store.phone,
				email: store.email,
				instagram: store.instagram,
				working_hours: store.working_hours,
			}),
		)
		navigate(`/stores/${store.slug || store.uuid}`)
	}

	return (
		<div className="min-h-screen bg-[#F7F8F8] flex flex-col">
			<Header currentPage="startseite" />

			<section className="relative overflow-hidden border-b border-[#0B224D0D]">
				<div className="absolute inset-0 bg-gradient-to-br from-[#E8EEF6] via-[#F7F8F8] to-[#DDE8DF]" />
				<div className="relative mx-auto max-w-5xl px-5 pt-10 pb-14 lg:pt-16 lg:pb-20 flex flex-col items-center text-center gap-5">
					<div className="flex items-center gap-3">
						<LogoIcon className="size-14 lg:size-16" />
						<div className="text-left">
							<p className="text-secondary text-2xl lg:text-4xl font-extrabold tracking-tight">
								minmini
							</p>
							<p className="text-muted-foreground text-sm lg:text-base font-medium">
								Finde deinen Store und miete ein Regal
							</p>
						</div>
					</div>

					<form
						onSubmit={onSearch}
						className="w-full max-w-3xl mt-2 grid gap-3 grid-cols-1 sm:grid-cols-[1fr_auto_auto] bg-white/90 backdrop-blur rounded-2xl p-3 shadow-sm border border-[#EEEFF0]">
						<Input
							placeholder="Stadt oder Name"
							value={city || q}
							onChange={e => {
								setCity(e.target.value)
								setQ(e.target.value)
							}}
							className="h-12 sm:col-span-1"
						/>
						<Input
							placeholder="PLZ"
							value={zip}
							onChange={e => setZip(e.target.value)}
							className="h-12 sm:w-28"
						/>
						<Button type="submit" className="h-12 w-full sm:w-auto" disabled={isFetching}>
							{isFetching ? <Loader2 className="animate-spin" /> : <Search className="size-4" />}
							Suchen
						</Button>
					</form>
					<button
						type="button"
						onClick={useNearMe}
						className="text-sm font-semibold text-secondary underline-offset-4 hover:underline inline-flex items-center gap-1.5">
						<MapPin className="size-4" />
						In meiner Nähe
					</button>
				</div>
			</section>

			<section className="mx-auto w-full max-w-5xl px-5 py-10 flex-1">
				<div className="flex items-end justify-between gap-3 mb-6">
					<div>
						<h2 className="text-secondary text-xl font-extrabold">Stores</h2>
						<p className="text-muted-foreground text-sm">
							{isLoading ? 'Laden…' : `${stores.length} aktive Store${stores.length === 1 ? '' : 's'}`}
						</p>
					</div>
					<button
						type="button"
						onClick={() => refetch()}
						className="text-sm font-medium text-muted-foreground hover:text-secondary">
						Aktualisieren
					</button>
				</div>

				{isLoading ? (
					<div className="flex justify-center py-16">
						<Loader2 className="animate-spin text-secondary" />
					</div>
				) : stores.length === 0 ? (
					<div className="rounded-2xl bg-white border border-[#EEEFF0] px-6 py-12 text-center">
						<p className="text-secondary font-bold">Keine Stores gefunden</p>
						<p className="text-muted-foreground text-sm mt-1">
							Passe die Suche an oder schau später wieder vorbei.
						</p>
					</div>
				) : (
					<ul className="grid gap-4 sm:grid-cols-2">
						{stores.map(store => (
							<li key={store.uuid}>
								<button
									type="button"
									onClick={() => openStore(store)}
									className="w-full text-left rounded-2xl bg-white border border-[#EEEFF0] hover:border-secondary/40 transition p-4 flex gap-4">
									<div className="size-16 rounded-xl bg-[#F6F6F6] overflow-hidden shrink-0 flex items-center justify-center">
										{store.logo_url ? (
											<img
												src={store.logo_url}
												alt=""
												className="size-full object-cover"
											/>
										) : (
											<LogoIcon className="size-8 opacity-60" />
										)}
									</div>
									<div className="min-w-0 flex-1">
										<p className="text-secondary font-extrabold truncate">{store.name}</p>
										<p className="text-muted-foreground text-sm truncate">
											{[store.zip, store.city].filter(Boolean).join(' ') || '—'}
										</p>
										<p className="text-xs text-muted-foreground mt-1 line-clamp-2">
											{formatHoursSnippet(store.working_hours)}
										</p>
									</div>
								</button>
							</li>
						))}
					</ul>
				)}

				<p className="mt-10 text-center text-sm text-muted-foreground">
					Schon einen Store gewählt?{' '}
					<Link to="/suchen" className="text-secondary font-semibold hover:underline">
						Artikel suchen
					</Link>
				</p>
			</section>

			<Footer />
		</div>
	)
}
